import { ApiResponse, Product, CartItem } from "../models";
import apiClient from "./api/apiClient";
import { handleApiError } from "./api/apiErrorHandler";

export interface GetProductsResponse extends ApiResponse {
    results: {
        products: Product[];
    };
}

export interface GetProductResponse extends ApiResponse {
    results: {
        product: Product;
    };
}

export interface CheckoutResponse extends ApiResponse {
    results: {
        proforma: any;
        paymentUrl: string;
    };
}

export interface SubmitWithoutPaymentResponse extends ApiResponse {
    results: {
        proforma: { uuid: string; [key: string]: any };
        invoice: { id: number; number_text?: string };
        requires_payment: false;
    };
}

export interface CheckProformaResponse extends ApiResponse {
    results: {
        isPaid: boolean;
        isFulfilled?: boolean;
        fulfilmentFailed?: boolean;
        nextAction?: 'pending' | 'book' | 'home' | 'fulfilment_failed';
        canCheckAgain?: boolean;
        canBook?: boolean;
        message?: string;
        bookableProfilePackageId?: number | null;
        error?: boolean;
        proforma?: any;
    };
}

class ShopService {
    async getProducts(customerId: number): Promise<GetProductsResponse> {
        return await apiClient
            .get(`${this.baseUrl}/products?customer_id=${customerId}`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve shop products.")
            );
    }

    async getProduct(productId: string | number, customerId: number): Promise<GetProductResponse> {
        return await apiClient
            .get(`${this.baseUrl}/products/${productId}?customer_id=${customerId}`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to retrieve product details.")
            );
    }

    async checkout(customerId: number, cart: CartItem[]): Promise<CheckoutResponse> {
        return await apiClient
            .post(`${this.baseUrl}/checkout?customer_id=${customerId}`, { cart })
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to complete checkout.")
            );
    }

    async submitWithoutPayment(customerId: number, cart: CartItem[]): Promise<SubmitWithoutPaymentResponse> {
        return await apiClient
            .post(`${this.baseUrl}/submit-without-payment?customer_id=${customerId}`, { cart })
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to submit order.")
            );
    }

    async checkProformaStatus(customerId: number, proformaId: string): Promise<CheckProformaResponse> {
        return await apiClient
            .get(`${this.baseUrl}/check-proforma?customer_id=${customerId}&proforma_id=${proformaId}`)
            .then(
                (response) => response.data,
                (error) => handleApiError(error, "Failed to check proforma status.")
            );
    }

    constructor(private readonly baseUrl: string) { }
}

export const shopService = new ShopService('/shop');
